// ============================================
// Configuration Constants for JLPT Flashcard App
// ============================================

import type { SRSState, SRSStateConfig } from '@/types';

// ============================================
// SRS State Rank (for field-level merge comparison)
// ============================================

/** Numeric rank for each SRS state — higher = more advanced.
 *  Used by mergeProgress to pick the winning state when two records diverge. */
export const SRS_RANK: Record<SRSState, number> = {
  new: 0,
  learning_1: 1,
  learning_2: 2,
  review_1: 3,
  review_2: 4,
  review_3: 5,
  mastered: 6
} as const;

// ============================================
// API Configuration
// ============================================

export const API_CONFIG = {
  ttsEndpoint: '/.netlify/functions/tts',
  rateLimit: 30, // requests per minute
  maxRetries: 3,
  retryBackoffMs: 1000, // exponential backoff base
  circuitBreakerThreshold: 3 // failures before opening circuit
} as const;

// ============================================
// Storage Configuration
// ============================================

export const STORAGE_CONFIG = {
  prefix: 'jlpt-',
  keys: {
    wordProgress: (level: string) => `jlpt-word-progress-${level}`,
    preferences: 'jlpt-preferences',
    audioCache: 'jlpt-audio-cache'
  },
  quotaWarningThreshold: 0.9, // 90% of quota
  version: 2 // for migration handling
} as const;

// ============================================
// SRS (Spaced Repetition) Configuration
// ============================================

export const SRS_CONFIG = {
  intervals: {
    new: 0,
    learning_1: 10 * 60 * 1000,        // 10 minutes
    learning_2: 60 * 60 * 1000,        // 1 hour
    review_1: 24 * 60 * 60 * 1000,     // 1 day
    review_2: 3 * 24 * 60 * 60 * 1000, // 3 days
    review_3: 7 * 24 * 60 * 60 * 1000, // 1 week
    mastered: 30 * 24 * 60 * 60 * 1000 // 30 days
  } as Record<SRSState, number>,

  // Progressions: softened drop-one-state model on incorrect at higher
  // levels. Earlier behavior reset to learning_1 on every wrong answer
  // past learning_2 — harsher than Anki SM-2 (×0.6 interval) and
  // demoralizing on edge slips. Strict reset is preserved at learning_1
  // (back to new) and learning_2 (back to learning_1) where the user
  // hasn't yet demonstrated lasting recall.
  progressions: {
    new: { correct: 'learning_1', incorrect: 'new' },
    learning_1: { correct: 'learning_2', incorrect: 'new' },
    learning_2: { correct: 'review_1', incorrect: 'learning_1' },
    review_1: { correct: 'review_2', incorrect: 'learning_1' },
    review_2: { correct: 'review_3', incorrect: 'review_1' },
    review_3: { correct: 'mastered', incorrect: 'review_2' },
    mastered: { correct: 'mastered', incorrect: 'review_3' }
  } as Record<SRSState, { correct: SRSState; incorrect: SRSState }>,

  stateConfig: {
    new: {
      name: 'New',
      color: '#A8C0CC',
      onColor: '#1F2A3A',
      indicator: 'N',
      description: "Words you haven't studied yet"
    },
    learning_1: {
      name: 'Learning',
      color: '#C9A24A',
      onColor: '#1F2A3A',
      indicator: 'L',
      description: "Words you're currently learning"
    },
    learning_2: {
      name: 'Learning',
      color: '#C9A24A',
      onColor: '#1F2A3A',
      indicator: 'L',
      description: "Words you're currently learning"
    },
    review_1: {
      name: 'Review',
      color: '#9A4848',
      onColor: '#FFFFFF',
      indicator: 'R',
      description: 'Words ready for review'
    },
    review_2: {
      name: 'Review',
      color: '#9A4848',
      onColor: '#FFFFFF',
      indicator: 'R',
      description: 'Words ready for review'
    },
    review_3: {
      name: 'Review',
      color: '#9A4848',
      onColor: '#FFFFFF',
      indicator: 'R',
      description: 'Words ready for review'
    },
    mastered: {
      name: 'Mastered',
      color: '#6F9B8E',
      onColor: '#1F2A3A',
      indicator: 'M',
      description: "Words you've mastered"
    }
  } as Record<SRSState, SRSStateConfig>,

  // Deck composition settings
  deckSize: 50,
  dueCardPercentage: 0.6, // 60% of deck should be due cards
  newCardsPerSession: 10
} as const;

// ============================================
// Audio Configuration
// ============================================

export const AUDIO_CONFIG = {
  cacheTTL: 30 * 24 * 60 * 60 * 1000, // 30 days
  maxCacheEntries: 500,
  preloadCount: 5,
  indexedDBName: 'jlpt-audio-cache',
  indexedDBVersion: 1
} as const;

// ============================================
// Quiz Configuration
// ============================================

export const QUIZ_CONFIG = {
  optionsCount: 4,
  sameTypeWeight: 0.7, // 70% chance of same type distractors
  speedChallengeSeconds: 8,
  feedbackDelayMs: 2000,
  particleExplanationDelayMs: 500,
  particleAdvanceDelayMs: 4000
} as const;

// ============================================
// Animation Durations (in ms)
// ============================================

export const ANIMATION_DURATION = {
  instant: 100,
  fast: 150,
  normal: 250,
  slow: 400,
  emphasis: 600
} as const;

// ============================================
// Keyboard Shortcuts
// ============================================

export const KEYBOARD_SHORTCUTS = {
  flipCard: [' ', 'Enter'],
  nextCard: ['ArrowRight'],
  prevCard: ['ArrowLeft'],
  toggleKana: ['k', 'K'],
  quizOptions: ['1', '2', '3', '4'],
  // Study-mode grading (only active after a flip — see useKeyboard). '1'/'2'
  // overlap with quizOptions, so the handler guards these to study mode to
  // avoid colliding with quiz answer selection.
  gradeAgain: ['1'],
  gradeGotIt: ['2'],
  escape: ['Escape']
} as const;

// ============================================
// External Links
// ============================================

export const EXTERNAL_LINKS = {
  paypalDonate: 'https://www.paypal.com/donate/?hosted_button_id=RDB6KTNXBLZ7Q'
} as const;

// ============================================
// Level Configuration
// ============================================

// Level badge backgrounds — Wada palette B chord. White text on N4
// (Old Gold), N5 (Verdigris), Hiragana (Pale Glaucous) fails WCAG AA;
// LEVEL_ON_COLORS provides the per-level text color so badges stay legible.
export const LEVEL_COLORS = {
  N5: '#6F9B8E',       // Verdigris
  N4: '#C9A24A',       // Old Gold
  N3: '#2D4A66',       // Old Indigo (primary)
  Everyday: '#9A4848', // Madder
  Hiragana: '#A8C0CC', // Pale Glaucous
  Katakana: '#5C6678'  // Cool grey (text-muted)
} as const;

export const LEVEL_ON_COLORS = {
  N5: '#1F2A3A',       // dark on Verdigris  → 4.66:1
  N4: '#1F2A3A',       // dark on Old Gold   → 6.03:1
  N3: '#FFFFFF',       // white on Indigo    → 9.20:1 AAA
  Everyday: '#FFFFFF', // white on Madder    → 6.19:1
  Hiragana: '#1F2A3A', // dark on Pale Glaucous → 7.64:1 AAA
  Katakana: '#FFFFFF'  // white on Cool grey  → 5.79:1
} as const;

// ============================================
// Time-based Theme Configuration
// ============================================

export const TIME_THEMES = {
  morning: { start: 6, end: 12, className: 'time-morning' },
  afternoon: { start: 12, end: 17, className: 'time-afternoon' },
  evening: { start: 17, end: 20, className: 'time-evening' },
  night: { start: 20, end: 6, className: 'time-night' }
} as const;

